import { SatisfactionSurveyFrequencyType } from '../../../services/modules/satisfactionSurveySettings/satisfactionSurveyFrequencyType.enum';
import { onlyNumbers } from '../../../utils/validators';
import { SatisfactionSurveySettingsSectionProps } from '../satisfactionSurveySettings.types';
import useSyncedFieldValue from './useSyncedFieldValue';

export const MIN_FREQUENCY_LAST_DAYS = 1;
export const MAX_FREQUENCY_LAST_DAYS = 365;
export const DEFAULT_FREQUENCY_LAST_DAYS = 180;

type UseSatisfactionSurveyFrequencyFieldsParams = Omit<
  SatisfactionSurveySettingsSectionProps,
  'isDisabled'
>;

const useSatisfactionSurveyFrequencyFields = ({
  settings,
  isSaving,
  rejectedSaves,
  onChange,
}: UseSatisfactionSurveyFrequencyFieldsParams) => {
  const isLastDays = settings.frequencyType === SatisfactionSurveyFrequencyType.LAST_DAYS;
  const savedLastDays = settings.frequencyLastDays ?? DEFAULT_FREQUENCY_LAST_DAYS;
  const lastDays = useSyncedFieldValue({
    savedValue: String(savedLastDays),
    rejectedSaves,
    isSaving,
  });

  const typedLastDays = Number(onlyNumbers(lastDays.value));
  const isOutOfRange =
    !lastDays.value ||
    typedLastDays < MIN_FREQUENCY_LAST_DAYS ||
    typedLastDays > MAX_FREQUENCY_LAST_DAYS;

  const onFrequencyTypeChange = (frequencyType: SatisfactionSurveyFrequencyType) => {
    const frequencyLastDays =
      frequencyType === SatisfactionSurveyFrequencyType.LAST_DAYS
        ? (settings.frequencyLastDays ?? DEFAULT_FREQUENCY_LAST_DAYS)
        : settings.frequencyLastDays;

    onChange({ frequencyType, frequencyLastDays });
  };

  const onLastDaysBlur = () => {
    const hasDiscardedTypedValue = lastDays.onBlur();

    if (hasDiscardedTypedValue) {
      return;
    }

    if (isOutOfRange) {
      return;
    }

    lastDays.setValue(String(typedLastDays));

    if (typedLastDays === settings.frequencyLastDays) {
      return;
    }

    onChange({ frequencyLastDays: typedLastDays });
  };

  return { isLastDays, lastDays, isOutOfRange, onFrequencyTypeChange, onLastDaysBlur };
};

export default useSatisfactionSurveyFrequencyFields;
